import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateTtbPaymentSettingsDocument,
    type CreateTtbPaymentSettingsMutation,
    type CreateTtbPaymentSettingsMutationVariables,
} from '../../../../api/mutations/createTtbPaymentSettings';
import { TtbPaymentFlag3ds, type TtbPaymentSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type AddTtbPaymentSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = TtbPaymentSettingsInput;

const initialValues: FormValues = {
    amount: 0,
    currency: '',
    displayName: '',
    audience: '',
    flag3ds: TtbPaymentFlag3ds.No,

    apiKey: '',
    officeId: '',
    paymentEndpoint: '',
    signingPrivateKey: '',
    signingPublicKey: '',
    encryptionPrivateKey: '',
    encryptionPublicKey: '',
    kid: '',
};

const AddTtbPaymentSettingsForm = ({ moduleId, onComplete }: AddTtbPaymentSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'ttbPaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const { ttbFlag3dsOptions } = useSystemOptions();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('ttbPaymentModuleDetails:paymentSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateTtbPaymentSettingsMutation, CreateTtbPaymentSettingsMutationVariables>({
                    mutation: CreateTtbPaymentSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('ttbPaymentModuleDetails:paymentSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator());

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addFiservPaymentSettings"
                    layout="vertical"
                    name="addFiservPaymentSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('ttbPaymentModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                            returnObjects: true,
                        })}
                        name="currency"
                    />
                    <InputNumberField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                            returnObjects: true,
                        })}
                        name="amount"
                    />
                    <Divider>{t('ttbPaymentModuleDetails:dividers.paymentSettings')}</Divider>
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.audience', {
                            returnObjects: true,
                        })}
                        name="audience"
                    />
                    <SelectField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.flag3ds', {
                            returnObjects: true,
                        })}
                        name="flag3ds"
                        options={ttbFlag3dsOptions}
                        showSearch
                    />
                    <PasswordField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', { returnObjects: true })}
                        name="apiKey"
                    />
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.officeId', {
                            returnObjects: true,
                        })}
                        name="officeId"
                    />
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint', {
                            returnObjects: true,
                        })}
                        name="paymentEndpoint"
                    />
                    <SingleSecretUploadField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.signingPrivateKey', {
                            returnObjects: true,
                        })}
                        name="signingPrivateKey"
                    />
                    <SingleSecretUploadField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.signingPublicKey', {
                            returnObjects: true,
                        })}
                        name="signingPublicKey"
                    />
                    <SingleSecretUploadField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.encryptionPrivateKey', {
                            returnObjects: true,
                        })}
                        name="encryptionPrivateKey"
                    />
                    <SingleSecretUploadField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.encryptionPublicKey', {
                            returnObjects: true,
                        })}
                        name="encryptionPublicKey"
                    />
                    <InputField
                        {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.kid', {
                            returnObjects: true,
                        })}
                        name="kid"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddTtbPaymentSettingsForm;
